import {
  useCallback,
  useContext,
} from 'react'

import { LocalForageContext } from '../../../../../../../contexts/localforage.context'
import useCartController from '../../../../../../../controllers/cart.controller'
import getProp from '../../../../../../../helpers/getProp'
import getAccessTokenV2 from '../../../../../../CheckoutV2/helpers/getAccessToken'
import { useShoppingCartContext } from '../../../../../providers/ShoppingCartProvider'
import getQuantity from '../../../../../../../helpers/getQuantity'
import { LocalContext } from '../../../../../../../helpers/useLocalContext'
import { TokenContext, getValidTokenV2 } from '../../../../../../../contexts/token.context'
import shoppingCartV2API from '../../../../../api'
import useExecuteInCatch from './useExecuteInCatch'
import nrLogger from '../../../../../../../helpers/nrLogger'
import checkInventory from '../checkInventory'
import executeGTM from '../executeGTM'
import { useProductOptionSequenceContext } from '../../../../../../../contexts/productOptionSequence.context'
import useDifmAction from '../../../../../../DIFM/store/hooks/useDifmAction'
import useIsDifmEnabled from '../../../../../../DIFM/store/hooks/useIsDifmEnabled'
import convertToOldStructureOrderData from '../../../../../../CheckoutV2/utility/convertToOldStructureOrderData'
import { useLocationContext } from '../../../../../../../contexts/location.context'
import { useEconomyCookie } from '../../../../../../../hooks/useEconomyFlag'
import { useUnfoldedShippingCookie } from '../../../../../../../hooks/useUnfoldedShipping'
import { useDynamicShippingCookie, useDynamicShippingEnabled } from '../../../../../../../hooks/useDynamicShipping'

const useProceedToCheckout = () => {
  const { actions } = useDifmAction()
  const { addCart, clearCart } = useCartController()
  const { localForageData } = useContext(LocalForageContext)
  const { localContext } = useContext(LocalContext)
  const { token, setToken } = useContext(TokenContext)
  const { history } = localContext
  const productOptionSequenceData = useProductOptionSequenceContext()
  const { skuArray = [] } = productOptionSequenceData
  const { deselectInstallation } = actions
  const isDifmEnabled = useIsDifmEnabled()
  const economy = useEconomyCookie()
  const isUnfoldedShipping = useUnfoldedShippingCookie()
  const isDynamicShipping = useDynamicShippingCookie()
  const isDynamicShippingEnabled = useDynamicShippingEnabled()

  const {
    cartData,
    getCartId,
    shouldShowLoader,
    setIsCartDataMerged,
    setItemProductIdBeingUpdated,
  } = useShoppingCartContext()

  const executeInCatch = useExecuteInCatch()
  const { city, postalCode, state } = useLocationContext()

  const triggerDifmShopReset = useCallback(() => {
    /**
     * Set difm data to false upon leaving cart and move to checkout
     * to avoid unneccesary selected shop once you remove it in checkout
     * and then abandon and go back to cart.
     */
    if (isDifmEnabled) deselectInstallation()
  }, [isDifmEnabled, deselectInstallation])

  const manageProceedToCheckout = useCallback(async (cartId, orderId, tokenDb) => {
    let response
    try {
      const shipmentData = {
        deliveryPostcode: postalCode,
        deliveryState: state,
        deliveryCity: city,
        deliveryCountry: 'US',
        shippingMethod: 'Ground',
        shippingProtection: true,
        ...economy ? { economy } : {},
        ...isUnfoldedShipping ? { isUnfoldedShipping } : {},
        ...isDynamicShipping ? { isDynamicShipping } : {},
      }
      const accessToken = await getValidTokenV2(tokenDb, setToken)
      const data = await shoppingCartV2API.orders.saveV2(cartId, accessToken, shipmentData)
      response = {
        results: convertToOldStructureOrderData(data?.data?.orders),
      }
    } catch (error) {
      const status = getProp(error, ['response', 'status'])
      if (status === 401) {
        let newToken = {}
        newToken = await getAccessTokenV2(true)
        response = await manageProceedToCheckout(cartId, orderId, newToken)
      }
      nrLogger.error('ShoppingCartV2.useProceedToCheckout.error', error)
    }

    return response
  }, [city, state, postalCode, setToken, economy, isUnfoldedShipping, isDynamicShipping])

  return useCallback(
    async (aCartData = null) => {
      // In most cases the cartData(value in provider) will be used,
      // Few cases we want to only checkout items in aCartData
      shouldShowLoader(true)
      setItemProductIdBeingUpdated(null)

      try {
        const { items: oItems, shippingAdjustment: shippingAdjustmentDetails = null } = aCartData && aCartData.items ? aCartData : cartData
        const hasOOS = checkInventory(oItems)
        const shippingAdjustment = isDynamicShippingEnabled && shippingAdjustmentDetails ? shippingAdjustmentDetails : null

        if (!hasOOS) {
          const cartId = getCartId()
          let orderId = (cartData && cartData.orderId) || 1
          if (localForageData?.cart?.orderId) {
            orderId = localForageData.cart.orderId
          }
          const response = await manageProceedToCheckout(cartId, orderId, token)
          // eslint-disable-next-line no-negated-condition
          if (!response.results.error) {
            const { results } = response
            clearCart()
            if (results && results.order) {
              const qty = getQuantity(results.orderItems)
              if (qty > 0) {
                addCart({
                  orderId: results.order.orderId,
                  cartQuantity: qty,
                })

                triggerDifmShopReset()

                executeGTM(oItems, skuArray, shippingAdjustment)
                setTimeout(() => {
                  history.push('/checkout')
                }, 500)
              } else {
                setIsCartDataMerged(true)
                history.push('/cart')
              }
            }
          } else {
            executeInCatch(response)
          }
        }
      } catch (error) {
        nrLogger.error('ShoppingCartV2.useProceedToCheckout.error', error)
      }
      shouldShowLoader(false)
    },
    [
      addCart,
      cartData,
      localForageData,
      clearCart,
      executeInCatch,
      getCartId,
      history,
      shouldShowLoader,
      setIsCartDataMerged,
      skuArray,
      triggerDifmShopReset,
      manageProceedToCheckout,
      setItemProductIdBeingUpdated,
      isDynamicShippingEnabled,
    ],
  )
}

export default useProceedToCheckout
